import { useCallback, useState } from "react";
import { Flame, Loader2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { getOrCreateViralInvite, getMyViralStats } from "@/lib/viralReferral.functions";
import { useLanguage } from "@/hooks/useLanguage";
import { useAuth } from "@/hooks/useAuth";
import { useTenant } from "@/lib/tenants/tenantContext";
import { localizedChallengeMessage } from "@/lib/tenants/experienceProfiles";

import { toCanonicalUrl } from "@/lib/share/canonicalShare";
import { withAutoReferral } from "@/lib/viralSprintV3/autoReferral";

function buildShareUrl(token: string): string {
  const base = `${toCanonicalUrl("/")}?ref=${encodeURIComponent(token)}`;
  return withAutoReferral(base, { source: "guerrilla", medium: "whatsapp", campaign: "invite" });
}

export function GuerrillaInviteButton({ compact = false }: { compact?: boolean }) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { tenant, profile } = useTenant();
  const [busy, setBusy] = useState(false);
  const stats = useQuery({
    queryKey: ["viral", "me", user?.id],
    queryFn: () => getMyViralStats(),
    enabled: !!user,
    refetchInterval: 30000,
  });

  const handleClick = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { token } = await getOrCreateViralInvite({ data: { source: "whatsapp" } });
      if (!token) return;
      const url = buildShareUrl(token);
      const baseMessage = t("guerrilla_share_message").replace("{url}", url);
      // Phase 7 — overlay localized tenant-flavored copy on top of the base i18n string.
      const message = localizedChallengeMessage(profile, url, tenant.tenant_name) || baseMessage;
      const wa = `https://wa.me/?text=${encodeURIComponent(message)}`;
      // Try native share first (mobile)
      if (typeof navigator !== "undefined" && navigator.share) {
        try {
          await navigator.share({ title: "VerseSport", text: message, url });
          return;
        } catch { /* fall through to wa.me */ }
      }
      window.open(wa, "_blank", "noopener,noreferrer");
    } finally {
      setBusy(false);
    }
  }, [busy, t]);

  const registered = stats.data?.registered ?? 0;

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      className={`group relative flex w-full items-center justify-center gap-2 rounded-2xl border border-primary/40 bg-gradient-to-r from-primary/20 via-primary/10 to-primary/20 px-4 ${compact ? "py-2.5" : "py-3.5"} font-black uppercase tracking-wider text-foreground shadow-lg transition-transform active:scale-[0.98]`}
      aria-label={t("guerrilla_invite_cta")}
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Flame className="h-5 w-5 text-[oklch(0.78_0.2_60)]" />}
      <span className="text-sm">Invita a un amigo</span>
      <span className="ml-1 rounded-full bg-primary/40 px-2 py-0.5 font-mono text-[10px] text-primary-foreground">
        +50 pts
      </span>
      {registered > 0 && (
        <span className="ml-1 rounded-full bg-primary/30 px-2 py-0.5 font-mono text-[10px]">
          {registered} {t("guerrilla_friends_joined")}
        </span>
      )}
    </button>
  );
}